import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { faSquareCheck, faSquareMinus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GlobalSearchResult } from "@/api/williams/search";
import { highlight } from "@/util";
import { IconButton } from "../../../components";
import { QualifierUser } from "@/api/user";
import { useDebounce } from "@/hooks";
import { useGlobalSearch } from "../../../search/hooks/useGlobalSearch";
import { useState } from "react";

export type TransferItemsProps = {
  transferFrom: QualifierUser;
  selectedItems: GlobalSearchResult[];
  setSelectedItems: (items: GlobalSearchResult[]) => void;
};

export function TransferItems(props: TransferItemsProps) {
  const [searchString, setSearchString] = useState<string>("");
  const debouncedSearch = useDebounce(searchString, 500);
  const search = useGlobalSearch({
    searchString: debouncedSearch,
    specialistId: props.transferFrom.id,
  });

  const data = search.data?.pages.flatMap((page) => page.results) ?? [];

  function handleItemSelected(item: GlobalSearchResult, selected: boolean) {
    if (selected) {
      props.setSelectedItems([...props.selectedItems, item]);
    } else {
      props.setSelectedItems(props.selectedItems.filter((x) => x.id !== item.id));
    }
  }

  return (
    <>
      <h4>Select Items</h4>
      <div className="row mt-2">
        <div className="col-lg-5">
          {/* Search */}
          <div className="input-group">
            <input
              type="text"
              placeholder="Search..."
              className="form-control"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
            />
            <span className="input-group-text" role="button" onClick={() => search.refetch()}>
              <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
            </span>
          </div>
        </div>
        <div className="col">
          <div className="d-none d-md-flex justify-content-end gap-2">
            <IconButton
              icon={faSquareCheck}
              title="Select All"
              disabled={data.length === 0}
              onClick={() => props.setSelectedItems(data)}
            />
            <IconButton
              icon={faSquareMinus}
              title="Clear All"
              disabled={data.length === 0}
              onClick={() => props.setSelectedItems([])}
            />
          </div>
        </div>
      </div>

      <table className="table mt-3">
        <thead className="table-light">
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th className="d-none d-md-table-cell">Description</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr key={item.id}>
              <td>{highlight(item.name, debouncedSearch)}</td>
              <td>{item.resultType}</td>
              <td className="d-none d-md-table-cell">{highlight(item.description, debouncedSearch)}</td>
              <td>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={props.selectedItems.includes(item)}
                  onChange={(e) => handleItemSelected(item, e.currentTarget.checked)}
                ></input>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {search.isLoading ? (
        <p>Loading...</p>
      ) : search.isError ? (
        <p>An unknown error occurred.</p>
      ) : search.isSuccess && data.length === 0 && searchString ? (
        <p>Your search returned no results.</p>
      ) : search.isSuccess && data.length === 0 ? (
        <p>Nothing found for this user.</p>
      ) : (
        <></>
      )}
    </>
  );
}
